import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { BankAccountsStore } from './bank-accounts.store';
import { ServiceAccessService } from '../services/service-access.service';
import { BankAccount } from './types';

export interface AccessibleService {
	_id: string;
	key: string;
	name: string;
	status: string;
}

/**
 * Store que gestiona los servicios disponibles para la cuenta bancaria seleccionada.
 * Se actualiza automáticamente cuando cambia la cuenta seleccionada.
 */
@Injectable({ providedIn: 'root' })
export class ServiceAccessStore {
	private bankAccountsStore = inject(BankAccountsStore);
	private serviceAccessService = inject(ServiceAccessService);

	private lastAccountNumber: string | undefined;
	private requestId = 0;

	// State
	accessibleServices = signal<AccessibleService[]>([]);
	loading = signal(false);
	error = signal<string | null>(null);

	// Computed helpers
	accessibleServiceKeys = computed(() => this.accessibleServices().map(s => s.key));

	/**
	 * Verifica si un servicio está disponible para la cuenta actual
	 */
	hasAccessToService = (serviceKey: string) => {
		return this.accessibleServiceKeys().includes(serviceKey);
	};

	constructor() {
		// React to account changes
		effect(() => {
			const accountNumber = this.bankAccountsStore.selectedBankAccountId();
			if (accountNumber && accountNumber !== this.lastAccountNumber) {
				// Initialize immediately from embedded data so guards work on first load
				const account = this.bankAccountsStore.selectedBankAccount();
				if (account?.accessibleServices?.length) {
					this.accessibleServices.set(account.accessibleServices);
				}
				// Always call the HTTP endpoint to keep services up to date
				this.loadServicesForAccount(accountNumber);
			}
		});
	}

	async loadServicesForAccount(accountNumber: string) {
		this.loading.set(true);
		this.error.set(null);
		const currentRequestId = ++this.requestId;

		try {
			const res = await this.serviceAccessService.getServicesForAccount(accountNumber);
			if (currentRequestId !== this.requestId) return; // stale response
			this.lastAccountNumber = accountNumber;
			if (res.data) {
				this.accessibleServices.set(Array.isArray(res.data) ? res.data : []);
			} else {
				this.accessibleServices.set([]);
			}
		} catch (err: any) {
			if (currentRequestId !== this.requestId) return; // stale response
			this.lastAccountNumber = undefined;
			this.error.set(err?.message || 'Error al cargar servicios disponibles');
			this.accessibleServices.set([]);
		} finally {
			if (currentRequestId === this.requestId) {
				this.loading.set(false);
			}
		}
	}

	/**
	 * Inicializa los servicios desde los datos embebidos en las cuentas bancarias.
	 * Se usa en setUserAndCustomer para tener los permisos disponibles de inmediato.
	 */
	initFromAccounts(bankAccounts: BankAccount[], selectedAccountNumber: string | undefined) {
		if (!bankAccounts?.length || !selectedAccountNumber) return;
		const account = bankAccounts.find(a => a.accountNumber === selectedAccountNumber);
		if (account?.accessibleServices?.length) {
			this.accessibleServices.set(account.accessibleServices);
		}
	}

	clear() {
		this.accessibleServices.set([]);
		this.lastAccountNumber = undefined;
		this.loading.set(false);
		this.error.set(null);
	}
}
